"use strict";
/**
 * Slot compatibility — pure logic for deciding whether one step's output
 * slot can feed another step's input slot.
 *
 * Consumed by the Pixi workflow-builder (insert-UI preview) and the
 * backend NodeInsertCapabilityService (sl-9fmv.5.2). Central matrix so
 * frontend and backend share a single truth table.
 *
 * Lossless coercion only: `text -> number` and friends are NOT tolerated
 * (parse risk); `number -> text` is a stringify (no information loss).
 * Attachment values can feed text slots as their display filename.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.SLOT_TYPE_COERCION_MATRIX = void 0;
exports.isDynamicBridge = isDynamicBridge;
exports.checkSlotCompatibility = checkSlotCompatibility;
exports.suggestCompatibleConsumers = suggestCompatibleConsumers;
/**
 * Assignable consumer types per producer type. Lossless only.
 *
 * Deliberately excluded (no fallbacks):
 *   - text -> number | boolean      (parse risk)
 *   - number | boolean | json -> attachment (ref-only target)
 *   - attachment -> number | json           (ref != scalar payload)
 *   - enum -> number | boolean       (enum values are strings)
 *
 * `enum -> enum` is only accepted when options match — that check lives
 * in `checkSlotCompatibility`, not the matrix.
 */
exports.SLOT_TYPE_COERCION_MATRIX = {
    text: new Set(['text']),
    number: new Set(['number', 'text']),
    boolean: new Set(['boolean', 'text']),
    enum: new Set(['enum', 'text']),
    json: new Set(['json', 'text']),
    attachment: new Set(['attachment', 'text']),
};
function optionsEqual(a, b) {
    if (!a || !b || a.length !== b.length)
        return false;
    const av = [...a.map(o => o.value)].sort();
    const bv = [...b.map(o => o.value)].sort();
    return av.every((v, i) => v === bv[i]);
}
/**
 * A dynamic bridge node accepts arbitrary upstream output at runtime
 * because its input shape is resolved by the executor, not declared on
 * the slot catalogue. Expressed as: `dynamicInputs === true` and no
 * bindable input slots.
 *
 * sl-abfd (B9): Slots that carry UI-only payloads — typed as
 * `bindable: false` (schema-editors, code editors, JSON editors) — are
 * rendered in the Inputs container but are NOT workflow inputs. They do
 * not block the dynamic-bridge classification. Using `bindable !== false`
 * as the filter aligns with every other consumer of `inputSlots`
 * (node-io, workflow-health, node-insert-capability).
 */
function isDynamicBridge(slots, dynamicFlag) {
    if (dynamicFlag !== true)
        return false;
    return slots.every(s => s.bindable === false);
}
/**
 * Strict slot-to-slot type compatibility.
 *
 * Returns `ok: true, coercion: 'direct'` when producer.type === consumer.type
 * (with enum options matching). Returns `ok: true, coercion: 'stringify'`
 * when the producer is assignable to `text`. Returns `ok: false` with a
 * human-readable reason otherwise.
 */
function checkSlotCompatibility(producer, consumer) {
    if (producer.type === 'enum' && consumer.type === 'enum') {
        if (optionsEqual(producer.options, consumer.options)) {
            return { ok: true, coercion: 'direct' };
        }
        const pOpts = (producer.options ?? []).map(o => o.value).join(', ');
        const cOpts = (consumer.options ?? []).map(o => o.value).join(', ');
        return {
            ok: false,
            reason: `Enum options mismatch: '${producer.name}' [${pOpts}] vs '${consumer.name}' [${cOpts}].`,
        };
    }
    const allowed = exports.SLOT_TYPE_COERCION_MATRIX[producer.type];
    if (!allowed.has(consumer.type)) {
        return {
            ok: false,
            reason: `Producer '${producer.name}' of type '${producer.type}' is not assignable to consumer '${consumer.name}' of type '${consumer.type}'.`,
        };
    }
    if (producer.type === consumer.type) {
        return { ok: true, coercion: 'direct' };
    }
    // Remaining allowed transitions target 'text'.
    return { ok: true, coercion: 'stringify' };
}
/**
 * For a given producer slot, return the slugs of candidate consumers that
 * could accept it. Sort key: direct matches first, then stringify-coerced,
 * then dynamic bridges. Within each group, alphabetical by slug so callers
 * get a stable order for UI rendering.
 */
function suggestCompatibleConsumers(producer, candidates) {
    const direct = [];
    const coerced = [];
    const dynamic = [];
    for (const c of candidates) {
        if (isDynamicBridge(c.inputSlots, c.dynamicInputs)) {
            dynamic.push(c.slug);
            continue;
        }
        // sl-abfd (B9): first BINDABLE slot, not first raw slot — UI-only
        // decorator slots (bindable:false, e.g. field-list-editor) are not
        // connection targets.
        const first = c.inputSlots.find(s => s.bindable !== false);
        if (!first)
            continue;
        const res = checkSlotCompatibility(producer, first);
        if (!res.ok)
            continue;
        if (res.coercion === 'direct')
            direct.push(c.slug);
        else
            coerced.push(c.slug);
    }
    direct.sort();
    coerced.sort();
    dynamic.sort();
    return [...direct, ...coerced, ...dynamic];
}
